import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../api";
import { label } from "../../labels";
import FamiliesPanel from "./FamiliesPanel";
import LinkResult from "./LinkResult";
import { btnGhost, btnPrimary, fmtDateTime, inputCls, panel } from "./ui";

function InviteForm({ title, help, endpoint, body, testId, onDone }) {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(endpoint, body(email.trim()));
      setResult(data);
      setErr("");
      setEmail("");
      onDone();
    } catch (ex) {
      setResult(null);
      setErr(formatApiError(ex));
    }
  };
  return (
    <div className={panel}>
      <h3 className="font-semibold text-[14px]">{title}</h3>
      <p className="text-[11px] text-[#132C44]/60 mt-1">{help}</p>
      <form onSubmit={submit} className="mt-3 flex gap-2 flex-wrap">
        <input type="email" required placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} flex-1 min-w-[200px]`} data-testid={`${testId}-email`} />
        <button type="submit" className={btnPrimary} data-testid={`${testId}-submit`}>Generar invitación</button>
      </form>
      {err && <div className="mt-2 text-[12px] text-[#9B2C3B]" data-testid={`${testId}-error`}>{err}</div>}
      <LinkResult result={result} testId={`${testId}-result`} />
    </div>
  );
}

function StudentAccounts({ accounts }) {
  const [links, setLinks] = useState({});
  const [err, setErr] = useState("");
  const reset = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/reset-link`);
      setLinks({ ...links, [id]: data });
    } catch (ex) {
      setErr(formatApiError(ex));
    }
  };
  const students = accounts.filter((a) => a.role === "student");
  if (!students.length) return null;
  return (
    <div className="space-y-2">
      {students.map((a) => (
        <div key={a.id} className="p-3 bg-white rounded-2xl border border-[#132C44]/10" data-testid={`eid-account-${a.email}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap text-[13px]">
            <span><strong>{a.email}</strong> · {label(a.role)} · último ingreso {fmtDateTime(a.lastLoginAt)}</span>
            <button onClick={() => reset(a.id)} className={btnGhost} data-testid={`eid-account-reset-${a.email}`}>Enlace de contraseña</button>
          </div>
          <LinkResult result={links[a.id]} testId={`eid-account-link-${a.email}`} />
        </div>
      ))}
      {err && <div className="text-[12px] text-[#9B2C3B]">{err}</div>}
    </div>
  );
}

export default function AccessPanel({ student }) {
  const [accounts, setAccounts] = useState([]);
  const [famKey, setFamKey] = useState(0);
  const load = useCallback(() => {
    api.get(`/students/${student.id}/accounts`).then(({ data }) => setAccounts(data));
    setFamKey((k) => k + 1);
  }, [student.id]);
  useEffect(() => {
    load();
  }, [load]);
  const hasStudentAccount = accounts.some((a) => a.role === "student");

  return (
    <div className="space-y-6" data-testid="eid-access-panel">
      <section className="space-y-3">
        <h3 className="font-serif text-xl font-bold">Cuenta del alumno</h3>
        <StudentAccounts accounts={accounts} />
        {!hasStudentAccount && (
          <InviteForm
            title="Invitar al alumno"
            help="Crea la cuenta del alumno y un enlace de activación de un solo uso (48 h)."
            endpoint={`/students/${student.id}/invite`}
            body={(email) => ({ email })}
            testId="eid-invite-student"
            onDone={load}
          />
        )}
      </section>
      <section className="space-y-3">
        <h3 className="font-serif text-xl font-bold">Acceso familiar</h3>
        <InviteForm
          title="Invitar a un familiar"
          help="Cuenta independiente, solo lectura de la información publicada. Si el email ya tiene acceso familiar, se le vincula este alumno."
          endpoint="/families/invite"
          body={(email) => ({ email, studentId: student.id })}
          testId="eid-invite-family"
          onDone={load}
        />
        <FamiliesPanel key={famKey} studentId={student.id} />
      </section>
    </div>
  );
}
